import React, { useRef, useEffect, useState, useCallback } from 'react';

// Sound effects using Web Audio API
const createAudioContext = () => {
  return new (window.AudioContext || window.webkitAudioContext)();
};

const playJumpSound = (audioCtx) => {
  if (!audioCtx) return;
  try {
    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(400, audioCtx.currentTime);
    oscillator.frequency.linearRampToValueAtTime(600, audioCtx.currentTime + 0.1);
    gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0, audioCtx.currentTime + 0.1);
    oscillator.start(audioCtx.currentTime);
    oscillator.stop(audioCtx.currentTime + 0.1);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

const playScoreSound = (audioCtx) => {
  if (!audioCtx) return;
  try {
    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(800, audioCtx.currentTime);
    oscillator.frequency.linearRampToValueAtTime(1200, audioCtx.currentTime + 0.1);
    gainNode.gain.setValueAtTime(0.2, audioCtx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0, audioCtx.currentTime + 0.1);
    oscillator.start(audioCtx.currentTime);
    oscillator.stop(audioCtx.currentTime + 0.1);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

const playGameOverSound = (audioCtx) => {
  if (!audioCtx) return;
  try {
    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    oscillator.type = 'sawtooth';
    oscillator.frequency.setValueAtTime(300, audioCtx.currentTime);
    oscillator.frequency.linearRampToValueAtTime(100, audioCtx.currentTime + 0.5);
    gainNode.gain.setValueAtTime(0.2, audioCtx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0, audioCtx.currentTime + 0.5);
    oscillator.start(audioCtx.currentTime);
    oscillator.stop(audioCtx.currentTime + 0.5);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

const playSlapSound = (audioCtx) => {
  if (!audioCtx) return;
  try {
    // Create noise for slap effect
    const bufferSize = audioCtx.sampleRate * 0.1; // 100ms of noise
    const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
    const data = buffer.getChannelData(0);
    
    // Generate noise burst
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 2);
    }
    
    const noise = audioCtx.createBufferSource();
    noise.buffer = buffer;
    
    // Low-pass filter for thump
    const filter = audioCtx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(800, audioCtx.currentTime);
    filter.frequency.exponentialRampToValueAtTime(200, audioCtx.currentTime + 0.1);
    
    const gainNode = audioCtx.createGain();
    gainNode.gain.setValueAtTime(0.6, audioCtx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.15);
    
    noise.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    
    noise.start(audioCtx.currentTime);
    noise.stop(audioCtx.currentTime + 0.15);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// Game constants
const GAME_WIDTH = 400;
const GAME_HEIGHT = 600;
const GRAVITY = 0.35;
const JUMP_FORCE = -7.5;
const OBSTACLE_WIDTH = 55;
const OBSTACLE_GAP = 200;
const OBSTACLE_SPEED = 2;
const SOCK_SIZE = 35;
const SOCK_X = 70;

export const SockGame = () => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState('start'); // 'start', 'ready', 'playing', 'dying', 'gameover'
  const [displayScore, setDisplayScore] = useState(0);
  const [lastScore, setLastScore] = useState(0);
  const [canRestart, setCanRestart] = useState(true);
  const [highScore, setHighScore] = useState(() => {
    const saved = localStorage.getItem('sockGameHighScore');
    return saved ? parseInt(saved, 10) : 0;
  });
  
  // All game state in refs to avoid closure issues
  const [muted, setMuted] = useState(() => localStorage.getItem('sockGameMuted') === 'true');
  const mutedRef = useRef(muted);
  const audioCtxRef = useRef(null);
  const animationRef = useRef(null);
  const gameStateRef = useRef('start');
  const sockRef = useRef({ y: GAME_HEIGHT / 2, velocity: 0 });
  const obstaclesRef = useRef([]);
  const scoreRef = useRef(0);
  const highScoreRef = useRef(highScore);
  const canRestartRef = useRef(true);
  const lastScoreRef = useRef(0);
  const hasPlayedRef = useRef(false);
  
  // Death animation state
  const deathAnimRef = useRef({
    startX: 0,
    startY: 0,
    rotation: 0,
    scale: 1,
    progress: 0,
    paused: false,
    pauseTime: 0
  });

  // Keep mutedRef in sync
  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  // Keep highScoreRef in sync
  useEffect(() => {
    highScoreRef.current = highScore;
  }, [highScore]);

  // Keep gameStateRef in sync
  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  // Keep canRestartRef in sync
  useEffect(() => {
    canRestartRef.current = canRestart;
  }, [canRestart]);

  const initAudio = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = createAudioContext();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
  }, []);

  const createObstacle = useCallback(() => {
    const minTopHeight = 70;
    const maxTopHeight = GAME_HEIGHT - OBSTACLE_GAP - 70;
    const topHeight = minTopHeight + Math.random() * (maxTopHeight - minTopHeight);
    
    const hangingItems = ['👕', '👗', '🧥', '👔'];
    const floorItems = ['👟', '🥾', '👠', '🧺'];
    
    return {
      x: GAME_WIDTH,
      topHeight,
      bottomY: topHeight + OBSTACLE_GAP,
      scored: false,
      topEmoji: hangingItems[Math.floor(Math.random() * hangingItems.length)],
      bottomEmoji: floorItems[Math.floor(Math.random() * floorItems.length)],
    };
  }, []);

  const checkCollision = useCallback((sockY) => {
    // Check boundaries (allow touching edges)
    if (sockY < -5 || sockY > GAME_HEIGHT - SOCK_SIZE + 5) {
      return true;
    }

    // Check obstacle collision with generous padding for fairness
    const padding = 12; // More forgiving hitbox
    const sockLeft = SOCK_X + padding;
    const sockRight = SOCK_X + SOCK_SIZE - padding;
    const sockTop = sockY + padding;
    const sockBottom = sockY + SOCK_SIZE - padding;

    for (const obstacle of obstaclesRef.current) {
      // Add small buffer to obstacles too
      const obstacleBuffer = 3;
      if (sockRight > obstacle.x + obstacleBuffer && sockLeft < obstacle.x + OBSTACLE_WIDTH - obstacleBuffer) {
        if (sockTop < obstacle.topHeight - obstacleBuffer || sockBottom > obstacle.bottomY + obstacleBuffer) {
          return true;
        }
      }
    }
    return false;
  }, []);

  const prepareGame = useCallback(() => {
    sockRef.current = { y: GAME_HEIGHT / 2, velocity: 0 };
    // Spawn first obstacle closer so it's visible
    const firstObstacle = createObstacle();
    firstObstacle.x = GAME_WIDTH - 120;
    obstaclesRef.current = [firstObstacle];
    scoreRef.current = 0;
    setDisplayScore(0);
    setGameState('ready');
  }, [createObstacle]);

  const startPlaying = useCallback(() => {
    // First jump starts the game
    sockRef.current.velocity = JUMP_FORCE;
    if (!mutedRef.current) playJumpSound(audioCtxRef.current);
    setGameState('playing');
  }, []);

  const endGame = useCallback(() => {
    if (!mutedRef.current) playGameOverSound(audioCtxRef.current);

    const finalScore = scoreRef.current;
    lastScoreRef.current = finalScore;
    setLastScore(finalScore);
    hasPlayedRef.current = true;
    
    if (finalScore > highScoreRef.current) {
      highScoreRef.current = finalScore;
      setHighScore(finalScore);
      localStorage.setItem('sockGameHighScore', finalScore.toString());
    }
    
    // Initialize death animation
    deathAnimRef.current = {
      startX: SOCK_X,
      startY: sockRef.current.y,
      rotation: 0,
      scale: 1,
      progress: 0,
      paused: false,
      pauseTime: 0,
      slapPlayed: false,
      finalAngle: Math.random() * 360 // Random final angle
    };
    
    setCanRestart(false);
    setGameState('dying');
  }, []);
  
  // Death animation completion handler
  const finishDeathAnimation = useCallback(() => {
    // Transition to ready state
    sockRef.current = { y: GAME_HEIGHT / 2, velocity: 0 };
    const firstObstacle = createObstacle();
    firstObstacle.x = GAME_WIDTH - 120;
    obstaclesRef.current = [firstObstacle];
    scoreRef.current = 0;
    setDisplayScore(0);
    setCanRestart(true);
    setGameState('ready');
  }, [createObstacle]);

  const jump = useCallback(() => {
    const currentState = gameStateRef.current;
    
    if (currentState === 'start') {
      initAudio();
      prepareGame();
    } else if (currentState === 'ready') {
      // First space press starts the game with a jump
      startPlaying();
    } else if (currentState === 'playing') {
      sockRef.current.velocity = JUMP_FORCE;
      if (!mutedRef.current) playJumpSound(audioCtxRef.current);
    } else if (currentState === 'gameover' && canRestartRef.current) {
      // Go directly to ready state (skip welcome screen)
      initAudio();
      prepareGame();
    }
  }, [initAudio, prepareGame, startPlaying]);

  // Main game loop - runs continuously
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const drawBackground = () => {
      // Wardrobe interior background - wood panel look
      ctx.fillStyle = '#F5E6D3'; // Light wood color
      ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
      
      // Parallax scrolling wood grain lines (move slowly to the right)
      const time = performance.now() * 0.01;
      ctx.strokeStyle = 'rgba(139, 90, 43, 0.12)';
      ctx.lineWidth = 1;
      for (let i = 0; i < 15; i++) {
        const baseX = (i * 35 + time) % (GAME_WIDTH + 50) - 25;
        ctx.beginPath();
        ctx.moveTo(baseX, 0);
        ctx.lineTo(baseX + Math.sin(i) * 8, GAME_HEIGHT);
        ctx.stroke();
      }
      
      // Wardrobe back panel shadow edges
      ctx.fillStyle = 'rgba(139, 90, 43, 0.2)';
      ctx.fillRect(0, 0, 8, GAME_HEIGHT);
      ctx.fillRect(GAME_WIDTH - 8, 0, 8, GAME_HEIGHT);
      
      // Floating dust particles drifting right (parallax)
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      for (let i = 0; i < 12; i++) {
        const speed = 0.3 + (i % 3) * 0.15; // Varying speeds for depth
        const x = ((i * 47 + time * speed * 5) % (GAME_WIDTH + 20)) - 10;
        const y = (i * 67 + Math.sin(time * 0.5 + i) * 10) % GAME_HEIGHT;
        const size = 1.5 + (i % 3);
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const drawObstacles = () => {
      obstaclesRef.current.forEach((obstacle) => {
        // TOP OBSTACLE - Wardrobe shelf with hanging clothes
        const shelfHeight = 12;
        
        // Shelf back panel (darker wood)
        ctx.fillStyle = '#8B5A2B';
        ctx.fillRect(obstacle.x, 0, OBSTACLE_WIDTH, obstacle.topHeight);
        
        // Wood grain on shelf panel
        ctx.strokeStyle = 'rgba(60, 30, 10, 0.3)';
        ctx.lineWidth = 1;
        for (let y = 10; y < obstacle.topHeight; y += 20) {
          ctx.beginPath();
          ctx.moveTo(obstacle.x, y);
          ctx.lineTo(obstacle.x + OBSTACLE_WIDTH, y + 3);
          ctx.stroke();
        }
        
        // Shelf bracket/bottom edge
        ctx.fillStyle = '#6B4423';
        ctx.fillRect(obstacle.x, obstacle.topHeight - shelfHeight, OBSTACLE_WIDTH, shelfHeight);
        
        // Shelf highlight
        ctx.fillStyle = '#A0724A';
        ctx.fillRect(obstacle.x, obstacle.topHeight - shelfHeight, OBSTACLE_WIDTH, 3);
        
        // Clothes hanging from shelf (use stored emoji)
        ctx.font = '24px serif';
        ctx.textAlign = 'center';
        ctx.fillText(obstacle.topEmoji, obstacle.x + OBSTACLE_WIDTH / 2, obstacle.topHeight - 20);
        
        // Border
        ctx.strokeStyle = '#4A3520';
        ctx.lineWidth = 2;
        ctx.strokeRect(obstacle.x, 0, OBSTACLE_WIDTH, obstacle.topHeight);

        // BOTTOM OBSTACLE - Drawer
        const drawerTop = obstacle.bottomY;
        const drawerHeight = GAME_HEIGHT - drawerTop;
        
        // Drawer body (wood)
        ctx.fillStyle = '#A67B5B';
        ctx.fillRect(obstacle.x, drawerTop, OBSTACLE_WIDTH, drawerHeight);
        
        // Drawer face panel
        ctx.fillStyle = '#C4A484';
        ctx.fillRect(obstacle.x + 4, drawerTop + 4, OBSTACLE_WIDTH - 8, drawerHeight - 8);
        
        // Items on top of drawer (use stored emoji)
        ctx.font = '22px serif';
        ctx.fillText(obstacle.bottomEmoji, obstacle.x + OBSTACLE_WIDTH / 2, drawerTop + 30);
        
        // Border
        ctx.strokeStyle = '#4A3520';
        ctx.lineWidth = 2;
        ctx.strokeRect(obstacle.x, drawerTop, OBSTACLE_WIDTH, drawerHeight);
      });
    };

    const drawSock = (y, velocity) => {
      ctx.save();
      ctx.translate(SOCK_X + SOCK_SIZE / 2, y + SOCK_SIZE / 2);
      const rotation = Math.max(-30, Math.min(30, velocity * 3));
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.font = '38px serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('🧦', 0, 0);
      ctx.restore();
    };

    const drawScore = (score) => {
      ctx.font = 'bold 48px Fredoka, sans-serif';
      ctx.textAlign = 'center';
      ctx.strokeStyle = '#1E1E1E';
      ctx.lineWidth = 4;
      ctx.strokeText(score.toString(), GAME_WIDTH / 2, 60);
      ctx.fillStyle = '#FEC8D8';
      ctx.fillText(score.toString(), GAME_WIDTH / 2, 60);
    };

    const gameLoop = () => {
      const currentGameState = gameStateRef.current;

      if (currentGameState === 'playing') {
        const sock = sockRef.current;

        // Physics
        sock.velocity += GRAVITY;
        sock.y += sock.velocity;

        // Move and score obstacles
        for (let i = 0; i < obstaclesRef.current.length; i++) {
          const obstacle = obstaclesRef.current[i];
          obstacle.x -= OBSTACLE_SPEED;

          // Score when sock passes obstacle center
          if (!obstacle.scored && obstacle.x + OBSTACLE_WIDTH < SOCK_X) {
            obstacle.scored = true;
            scoreRef.current++;
            setDisplayScore(scoreRef.current);
            if (!mutedRef.current) playScoreSound(audioCtxRef.current);
          }
        }

        // Remove off-screen obstacles
        obstaclesRef.current = obstaclesRef.current.filter(o => o.x > -OBSTACLE_WIDTH);

        // Add new obstacles
        const lastObstacle = obstaclesRef.current[obstaclesRef.current.length - 1];
        if (!lastObstacle || lastObstacle.x < GAME_WIDTH - 220) {
          obstaclesRef.current.push(createObstacle());
        }

        // Check collision
        if (checkCollision(sock.y)) {
          endGame();
        }

        // Draw game
        drawBackground();
        drawObstacles();
        drawSock(sock.y, sock.velocity);
        drawScore(scoreRef.current);
      } else if (currentGameState === 'ready') {
        // Draw level preview with "Press Space" text
        drawBackground();
        drawObstacles();
        drawSock(sockRef.current.y, 0);
        
        // Draw "Press Space" text
        ctx.font = 'bold 36px Fredoka, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.strokeStyle = '#1E1E1E';
        ctx.lineWidth = 4;
        ctx.strokeText('Press Space', GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20);
        ctx.fillStyle = '#FEC8D8';
        ctx.fillText('Press Space', GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20);
        
        // Show scores after we've played at least once
        if (hasPlayedRef.current) {
          ctx.font = 'bold 20px Fredoka, sans-serif';
          ctx.lineWidth = 3;
          
          // Last score
          const lastText = `Last: ${lastScoreRef.current}`;
          ctx.strokeText(lastText, GAME_WIDTH / 2, GAME_HEIGHT / 2 + 30);
          ctx.fillStyle = '#957DAD';
          ctx.fillText(lastText, GAME_WIDTH / 2, GAME_HEIGHT / 2 + 30);
          
          // Best score
          const bestText = `Best: ${highScoreRef.current}`;
          ctx.strokeStyle = '#1E1E1E';
          ctx.strokeText(bestText, GAME_WIDTH / 2, GAME_HEIGHT / 2 + 60);
          ctx.fillStyle = '#D1FAE5';
          ctx.fillText(bestText, GAME_WIDTH / 2, GAME_HEIGHT / 2 + 60);
        }
      } else if (currentGameState === 'dying') {
        // Death animation - sock spins and zooms to center
        const anim = deathAnimRef.current;
        
        // Handle pause at the end
        if (anim.paused) {
          anim.pauseTime += 16; // ~60fps frame time
          if (anim.pauseTime >= 200) {
            // Pause complete, finish animation
            finishDeathAnimation();
          } else {
            // Draw paused state (sock in face) with random final angle
            drawBackground();
            ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
            ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
            
            ctx.save();
            ctx.translate(GAME_WIDTH / 2, GAME_HEIGHT / 2);
            ctx.rotate((anim.finalAngle * Math.PI) / 180);
            ctx.scale(anim.scale, anim.scale);
            ctx.font = '38px serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('🧦', 0, 0);
            ctx.restore();
          }
        } else {
          anim.progress += 0.035; // Animation speed
          
          if (anim.progress >= 1) {
            // Animation complete - start pause and play slap
            anim.paused = true;
            anim.pauseTime = 0;
            if (!anim.slapPlayed) {
              if (!mutedRef.current) playSlapSound(audioCtxRef.current);
              anim.slapPlayed = true;
            }
          } else {
            // Easing function for smooth animation
            const easeOut = 1 - Math.pow(1 - anim.progress, 3);
            
            // Calculate animated position (move to center)
            const targetX = GAME_WIDTH / 2;
            const targetY = GAME_HEIGHT / 2;
            const currentX = anim.startX + (targetX - anim.startX) * easeOut;
            const currentY = anim.startY + (targetY - anim.startY) * easeOut;
            
            // Spin faster as animation progresses (multiple rotations)
            anim.rotation += 12 + anim.progress * 25;
            
            // Scale up dramatically - bigger than the game area
            const targetScale = 12;
            anim.scale = 1 + (targetScale - 1) * easeOut;
            
            // Draw background and frozen obstacles (slightly faded)
            drawBackground();
            ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
            ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
            drawObstacles();
            
            // Draw spinning, scaling sock
            ctx.save();
            ctx.translate(currentX, currentY);
            ctx.rotate((anim.rotation * Math.PI) / 180);
            ctx.scale(anim.scale, anim.scale);
            ctx.font = '38px serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('🧦', 0, 0);
            ctx.restore();
            
            // Fade to white near the end
            if (anim.progress > 0.6) {
              const fadeProgress = (anim.progress - 0.6) / 0.4;
              ctx.fillStyle = `rgba(255, 255, 255, ${fadeProgress * 0.7})`;
              ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
            }
          }
        }
      } else if (currentGameState === 'gameover') {
        // Brief flash showing game over state - will auto-transition to ready
        drawBackground();
        drawObstacles();
        drawSock(sockRef.current.y, sockRef.current.velocity);
      } else {
        // Draw static screen for start/gameover
        drawBackground();
        ctx.font = '50px serif';
        ctx.textAlign = 'center';
        ctx.fillText('🧦', GAME_WIDTH / 2, GAME_HEIGHT / 2);
      }

      animationRef.current = requestAnimationFrame(gameLoop);
    };

    animationRef.current = requestAnimationFrame(gameLoop);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [createObstacle, checkCollision, endGame, finishDeathAnimation]);

  // Keyboard handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        jump();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [jump]);

  const toggleMute = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    setMuted(prev => {
      const next = !prev;
      mutedRef.current = next;
      localStorage.setItem('sockGameMuted', next.toString());
      return next;
    });
  }, []);

  const handleInteraction = (e) => {
    e.preventDefault();
    e.stopPropagation();
    jump();
  };

  return (
    <div 
      className="relative w-full max-w-md aspect-[2/3] border-4 border-[#1E1E1E] rounded-3xl shadow-[8px_8px_0px_#1E1E1E] overflow-hidden bg-[#EDE9FE] cursor-pointer select-none"
      onClick={handleInteraction}
      onTouchStart={handleInteraction}
      data-testid="game-container"
    >
      <button
        className="absolute top-3 right-3 z-10 bg-white/80 border-2 border-[#1E1E1E] rounded-xl w-10 h-10 flex items-center justify-center text-xl shadow-[3px_3px_0px_#1E1E1E] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_#1E1E1E] active:translate-y-0.5 active:shadow-none transition-all"
        onClick={toggleMute}
        onTouchStart={toggleMute}
        data-testid="mute-button"
        aria-label={muted ? 'Unmute' : 'Mute'}
      >
        {muted ? '🔇' : '🔊'}
      </button>
      <canvas
        ref={canvasRef}
        width={GAME_WIDTH}
        height={GAME_HEIGHT}
        className="w-full h-full"
        data-testid="game-canvas"
      />

      {/* Start Screen Overlay */}
      {gameState === 'start' && (
        <div 
          className="absolute inset-0 flex items-center justify-center backdrop-blur-sm bg-white/50"
          data-testid="start-screen"
        >
          <div className="bg-white border-4 border-[#1E1E1E] rounded-2xl p-8 shadow-[8px_8px_0px_#1E1E1E] flex flex-col items-center gap-4 mx-4">
            <h1 className="font-fredoka text-3xl sm:text-4xl font-bold text-[#1E1E1E] text-center">
              Lost Sock 🧦
            </h1>
            <p className="font-nunito text-base text-[#1E1E1E] text-center max-w-xs">
              Help the lonely sock navigate through the messy room to find its owner!
            </p>
            <div className="text-4xl animate-bounce mt-2">🧦</div>
            <button 
              className="bg-[#FEC8D8] border-2 border-[#1E1E1E] rounded-xl px-8 py-3 text-[#1E1E1E] font-bold text-xl shadow-[4px_4px_0px_#1E1E1E] hover:-translate-y-1 hover:shadow-[6px_6px_0px_#1E1E1E] active:translate-y-1 active:shadow-none transition-all font-fredoka tracking-wide uppercase"
              onClick={handleInteraction}
              data-testid="play-button"
            >
              Play
            </button>
            <p className="font-nunito text-sm text-[#957DAD]">
              Tap, Click or Press Space to jump
            </p>
            {highScore > 0 && (
              <p className="font-fredoka text-lg text-[#957DAD]" data-testid="high-score-display">
                Best: {highScore}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Game Over is now handled by going directly to ready state */}
    </div>
  );
};

export default SockGame;
